import {
  BuilderPresets,
  BuilderStyle,
  CompRef,
  SingleLayout,
} from '@wix/document-services-types';
import { EditorMethodArgs } from '../types';
import { HostAPIEditorX } from './HostAPIEditorX';
import { StyleParam, TPAPublicDataValue } from '@wix/editor-platform-sdk-types';
import type { AppsNamespace, ToolsNamespace } from '@wix/workspace';
import type { EditorPointer } from '@wix/editor-document-logic-api';

type DataItem = Record<string, any>;

type StyleItem = any;

type Color = any;

type ThemeColor = any;

type Font = any;

type ThemeFont = any;

type WixMedia = any;

// will be defined in data RFC
type LinkData = any;

type ThemeData = {
  colors: Record<string, Color | ThemeColor>;
  fonts: Record<string, Font | ThemeFont>;
};

type SelectedLinks = LinkData[];

type SelectedMedias = WixMedia[];

type SelectedColors = (Color | ThemeColor)[];

export interface ExternalPanelsPrivateAPI {
  // Retrieves the data of the bound component
  getData: () => Promise<DataItem>;
  // Sets data for the bound component
  setData: (newData: DataItem) => Promise<void>;
  // Retrieves the style of the bound component
  getStyle: () => Promise<StyleItem>;
  // Sets style for the bound component
  setStyle: (newStyle: StyleItem) => Promise<void>;
  // Retrieves the host theme
  getTheme: () => Promise<ThemeData>;
  // Returns media results chosen by the user
  selectMedia: (
    ...args: EditorMethodArgs<'openMediaPanel'>
  ) => Promise<SelectedMedias>;
  // Returns link results chosen by the user
  selectLink: (
    ...args: EditorMethodArgs<'openLinkPanel'>
  ) => Promise<SelectedLinks>;
  // Returns color results chosen by the user
  selectColor: (
    ...args: EditorMethodArgs<'openColorPicker'>
  ) => Promise<SelectedColors>;
  // Translates custom keys using the system i18n configuration
  translate: (
    key: string | string[],
    values?: Record<string, any>,
  ) => Promise<string | string[]>;
}

export class HostAPIStudio extends HostAPIEditorX {
  getExternalPanelsPrivateAPI = () => {
    return this.platformApiMethods.editor.getExternalPanelsPrivateAPI();
  };

  components = {
    data: {
      getData: (editorPointer: EditorPointer) => {
        return this.platformApiMethods.document.components.data?.getData(
          editorPointer,
        );
      },
      setData: (
        editorPointer: EditorPointer,
        data: Record<string, unknown>,
      ) => {
        return this.platformApiMethods.document.components.data?.setData(
          editorPointer,
          data,
        );
      },
    },
    responsiveLayout: {
      update: (
        editorPointer: EditorPointer,
        responsiveLayout: SingleLayout,
      ) => {
        return this.platformApiMethods.document.components.responsiveLayout?.update(
          editorPointer,
          responsiveLayout,
        );
      },
    },
    cssStyle: {
      get: (
        editorPointer: EditorPointer,
        options?: {
          customGetDefaultStyle: (editorPointer: EditorPointer) => {
            cssProperties: Record<string, unknown>;
            cssCustomProperties: Record<string, unknown>;
          };
        },
      ) => {
        return this.platformApiMethods.document.components.cssStyle?.get(
          editorPointer,
          options,
        );
      },
      update: (
        editorPointer: EditorPointer,
        cssStyle: Partial<BuilderStyle>,
        shouldRemoveRelatedProperties?: boolean,
      ) => {
        return this.platformApiMethods.document.components.cssStyle?.update(
          editorPointer,
          cssStyle,
          shouldRemoveRelatedProperties,
        );
      },
      remove: (editorPointer: EditorPointer, paths: string[]) => {
        return this.platformApiMethods.document.components.cssStyle?.remove(
          editorPointer,
          paths,
        );
      },
    },
    presets: {
      getEffective: (editorPointer: EditorPointer) => {
        return this.platformApiMethods.document.components.presets.getEffective(
          editorPointer,
        );
      },
      get: (editorPointer: EditorPointer) => {
        return this.platformApiMethods.document.components.presets.get(
          editorPointer,
        );
      },
      set: (
        editorPointer: EditorPointer,
        presetName: BuilderPresets['presetName'],
      ) => {
        return this.platformApiMethods.document.components.presets.set(
          editorPointer,
          presetName,
        );
      },
    },
  };

  tpa = {
    style: {
      // Effective methods - result will include cascade logic applied values
      getEffective: (editorPointer: EditorPointer<CompRef>) => {
        return this.platformApiMethods.document.tpa.style.getEffective(
          editorPointer.documentPointer,
        );
      },
      // Non-effective methods - result will not include cascade logic applied values, only the values set on the component given variants/scopes
      getNonEffective: (editorPointer: EditorPointer<CompRef>) => {
        return this.platformApiMethods.document.tpa.style.getNonEffective(
          editorPointer.documentPointer,
        );
      },
      setWithoutSideEffects: (
        editorPointer: EditorPointer<CompRef>,
        styleParams: StyleParam[],
      ) => {
        return this.platformApiMethods.document.tpa.style.setWithoutSideEffects(
          editorPointer.documentPointer,
          styleParams,
        );
      },
    },
    data: {
      comp: {
        get: (
          editorPointer: EditorPointer<CompRef>,
          key: string,
          callback: (value: Record<string, string>) => void,
        ) => {
          return this.platformApiMethods.document.tpa.data.comp.get(
            editorPointer.documentPointer,
            key,
            callback,
          );
        },
      },
      setMultiple: (
        editorPointer: EditorPointer<CompRef>,
        data: Record<string, unknown>,
      ) => {
        return this.platformApiMethods.document.tpa.data.setMultiple(
          editorPointer.documentPointer,
          data as Record<string, TPAPublicDataValue>,
          'COMPONENT',
        );
      },
    },
  };

  workspace: { apps: AppsNamespace; tools: ToolsNamespace } = {
    apps: {
      startInstallFlow: async (args) => {
        return this.platformApiMethods.editor.workspace!.startInstallFlow(args);
      },
      startPurchasePlanFlow: async (args) => {
        return this.platformApiMethods.editor.workspace!.startPurchasePlanFlow(
          args,
        );
      },
      startUpdateFlow: async (args) => {
        return this.platformApiMethods.editor.workspace!.startUpdateFlow(args);
      },
      openContactSupport: async (args) => {
        return this.platformApiMethods.editor.workspace!.openContactSupport(
          args,
        );
      },
    },
    tools: {
      editWithWixel: async (args) => {
        return this.platformApiMethods.editor.workspace!.editWithWixel(args);
      },
    },
  };
}
